import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  FileSpreadsheet,
  Banknote,
  Truck,
  CheckCircle2,
  Clock,
  TrendingUp,
  Users,
  AlertCircle,
  ClipboardList,
  User,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, hasRole, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [pendingTasks, setPendingTasks] = useState([]);
  const [jobsheetSummary, setJobsheetSummary] = useState({ data: [], totals: { assigned: 0, completed: 0, pending: 0 } });
  const [loading, setLoading] = useState(true);
  const [downloadingJobsheet, setDownloadingJobsheet] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, tasksRes, jobsheetRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/pending-tasks`),
        axios.get(`${API}/dashboard/jobsheet-summary`)
      ]);

      setStats(statsRes.data);
      setPendingTasks(tasksRes.data);
      setJobsheetSummary(jobsheetRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task) => {
    if (task.type === 'fund_request') {
      navigate(`/fund-requests`);
    } else if (task.type === 'material_request') {
      navigate(`/material-requests`);
    } else if (task.type === 'approved_fund_request') {
      navigate(`/fund-requests`);
    }
  };

  const handleDownloadJobsheetSummary = async () => {
    setDownloadingJobsheet(true);
    try {
      const response = await axios.get(`${API}/dashboard/jobsheet-summary/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Jobsheet_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Jobsheet Summary downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download Jobsheet Summary');
    } finally {
      setDownloadingJobsheet(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card
      className="p-6 hover:shadow-xl transition-all duration-300 border-0 hover:scale-[1.02] cursor-pointer glass-card"
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{title}</p>
          <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color }}>
            {value !== undefined ? value : '-'}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 font-medium">{subtitle}</p>
          )}
        </div>
        <div
          className="p-4 rounded-2xl shadow-md"
          style={{ 
            background: `linear-gradient(135deg, ${color}15, ${color}30)`,
          }}
        >
          <Icon className="h-7 w-7" style={{ color }} />
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1B273D] to-[#2d3e5f] rounded-xl p-6 md:p-8 shadow-lg text-white relative">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Home
              </h1>
              <p className="text-gray-200 text-sm md:text-lg">
                Welcome back, {user?.name}! Here's your overview.
              </p>
            </div>
            <div className="flex-shrink-0 ml-4">
              <NotificationBell />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {hasRole('Site Supervisor') && (
            <>
              <StatCard
                title="My Quotes"
                value={stats.my_quotes}
                icon={FileSpreadsheet}
                color="hsl(262.1 83.3% 57.8%)"
              />
              <StatCard
                title="Fund Requests"
                value={stats.my_fund_requests}
                icon={Banknote}
                color="hsl(142.1 76.2% 36.3%)"
              />
              <StatCard
                title="Material Requests"
                value={stats.my_material_requests}
                icon={Truck}
                color="hsl(199 89% 48%)"
              />
              <StatCard
                title="Pending Actions"
                value={(stats.pending_fund_requests || 0) + (stats.pending_material_requests || 0)}
                icon={Clock}
                color="hsl(47.9 95.8% 53.1%)"
              />
            </>
          )}

          {hasRole('Project Head') && (
            <>
              <StatCard
                title="Total Quotes"
                value={stats.total_quotes}
                icon={FileSpreadsheet}
                color="hsl(262.1 83.3% 57.8%)"
              />
              <StatCard
                title="Pending Approvals"
                value={stats.pending_fund_requests}
                icon={Clock}
                color="hsl(47.9 95.8% 53.1%)"
              />
              <StatCard
                title="Approved Requests"
                value={stats.approved_fund_requests}
                icon={CheckCircle2}
                color="hsl(142.1 76.2% 36.3%)"
              />
              <StatCard
                title="Total Approved Amount"
                value={`₹${(stats.total_approved_amount || 0).toLocaleString('en-IN')}`}
                icon={TrendingUp}
                color="hsl(199 89% 48%)"
                subtitle="Cumulative"
              />
            </>
          )}

          {hasRole('Delivery Manager') && (
            <>
              <StatCard
                title="Pending Materials"
                value={stats.pending_material_requests}
                icon={Clock}
                color="hsl(47.9 95.8% 53.1%)"
              />
              <StatCard
                title="Approved"
                value={stats.approved_material_requests}
                icon={CheckCircle2}
                color="hsl(142.1 76.2% 36.3%)"
              />
              <StatCard
                title="Rejected"
                value={stats.rejected_material_requests}
                icon={AlertCircle}
                color="hsl(346.8 77.2% 49.8%)"
              />
            </>
          )}

          {hasRole('Key Account Manager') && (
            <>
              <StatCard
                title="My Uploaded Quotes"
                value={stats.my_uploaded_quotes}
                icon={FileSpreadsheet}
                color="hsl(262.1 83.3% 57.8%)"
              />
              <StatCard
                title="Total Fund Requests"
                value={stats.total_fund_requests}
                icon={Banknote}
                color="hsl(142.1 76.2% 36.3%)"
              />
            </>
          )}

          {hasRole('Account Manager') && (
            <>
              <StatCard
                title="Approved Fund Requests"
                value={stats.approved_fund_requests}
                icon={CheckCircle2}
                color="hsl(142.1 76.2% 36.3%)"
                subtitle="Ready for processing"
              />
              <StatCard
                title="Total Approved Amount"
                value={stats.total_approved_amount ? `₹${stats.total_approved_amount.toLocaleString('en-IN')}` : '₹0'}
                icon={Banknote}
                color="#DC143C"
                subtitle="Funds to be disbursed"
              />
            </>
          )}

          {hasRole('Super Admin') && (
            <>
              <StatCard
                title="Total Users"
                value={stats.total_users}
                icon={Users}
                color="hsl(262.1 83.3% 57.8%)"
              />
              <StatCard
                title="Total Quotes"
                value={stats.total_quotes}
                icon={FileSpreadsheet}
                color="hsl(199 89% 48%)"
              />
              <StatCard
                title="Fund Requests"
                value={stats.total_fund_requests}
                icon={Banknote}
                color="hsl(142.1 76.2% 36.3%)"
              />
              <StatCard
                title="Material Requests"
                value={stats.total_material_requests}
                icon={Truck}
                color="hsl(47.9 95.8% 53.1%)"
              />
            </>
          )}
        </div>

        {/* Jobsheet Summary Table */}
        <Card className="overflow-hidden shadow-lg border-0" data-testid="jobsheet-summary-table">
          <div className="bg-gradient-to-r from-[#1B273D] to-[#2d3e5f] p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                  <ClipboardList className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Jobsheet Summary
                  </h2>
                  <p className="text-gray-300 text-sm mt-1">
                    {jobsheetSummary.is_filtered ? 'Your jobsheet overview' : 'Overview by Site Supervisor'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDownloadJobsheetSummary}
                disabled={downloadingJobsheet || jobsheetSummary.data.length === 0}
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white"
                title="Download to Excel"
                data-testid="download-jobsheet-summary-btn"
              >
                <Download className={`h-5 w-5 ${downloadingJobsheet ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-50">
                  <th className="px-6 py-4 text-left">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-[#1B273D]" />
                      <span className="text-sm font-bold text-[#1B273D] uppercase tracking-wider">
                        Name of the Supervisor
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center">
                    <span className="text-sm font-bold text-[#1B273D] uppercase tracking-wider">
                      No. of Jobsheet Assigned
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center">
                    <span className="text-sm font-bold text-[#1B273D] uppercase tracking-wider">
                      No. of Jobsheet Completed
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center">
                    <span className="text-sm font-bold text-[#1B273D] uppercase tracking-wider">
                      No. of Jobsheet Pending
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {jobsheetSummary.data.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <ClipboardList className="h-10 w-10 text-gray-300" />
                        <p className="text-gray-500 font-medium">No jobsheet data available</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {jobsheetSummary.data.map((row, index) => (
                      <tr 
                        key={index} 
                        className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all duration-200"
                        data-testid={`jobsheet-row-${index}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#DC143C] to-[#b91130] flex items-center justify-center shadow-md">
                              <span className="text-white font-bold text-sm">
                                {row.supervisor_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-semibold text-gray-800">{row.supervisor_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-base px-4 py-1 font-bold">
                            {row.assigned}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-base px-4 py-1 font-bold">
                            {row.completed}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge className={`text-base px-4 py-1 font-bold ${
                            row.pending > 0 
                              ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                          }`}>
                            {row.pending}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {/* Total Row */}
                    <tr className="bg-gradient-to-r from-[#1B273D] to-[#2d3e5f] text-white font-bold" data-testid="jobsheet-total-row">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <TrendingUp className="h-5 w-5 text-white" />
                          </div>
                          <span className="text-lg">Total</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {jobsheetSummary.totals.assigned}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-2xl text-green-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {jobsheetSummary.totals.completed}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-2xl text-amber-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {jobsheetSummary.totals.pending}
                        </span>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pending Tasks & Notifications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Tasks */}
          {pendingTasks.length > 0 && (
            <Card className="p-6 shadow-lg border-0 glass-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-red-100/80 rounded-xl backdrop-blur-sm">
                  <Clock className="h-6 w-6 text-[#DC143C]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Pending Tasks
                </h2>
              </div>
              <div className="space-y-3">
                {pendingTasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between p-5 rounded-xl glass-item cursor-pointer"
                    onClick={() => handleTaskClick(task)}
                    data-testid={`pending-task-${index}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-base">{task.title}</p>
                      <p className="text-sm text-gray-600 mt-1 break-all whitespace-pre-wrap line-clamp-2">{task.description}</p>
                    </div>
                    <Badge 
                      className="ml-3"
                      style={{
                        backgroundColor: task.type === 'fund_request' ? '#10b981' : 
                                        task.type === 'approved_fund_request' ? '#DC143C' : '#1B273D',
                        color: 'white'
                      }}
                    >
                      {task.type === 'fund_request' ? '💰 Fund' : 
                       task.type === 'approved_fund_request' ? '✅ Approved' : '🚚 Material'}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-white to-gray-50/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-[#DC143C] bg-opacity-10 rounded-xl">
              <TrendingUp className="h-6 w-6 text-[#DC143C]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quick Actions
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {hasAnyRole(['Key Account Manager', 'Super Admin']) && (
              <Button 
                onClick={() => navigate('/quotes/upload')} 
                data-testid="quick-action-upload-quote"
                className="bg-gradient-to-r from-[#DC143C] to-[#b91130] hover:from-[#b91130] hover:to-[#8b0e25] text-white shadow-md hover:shadow-lg transition-all duration-200 px-6 py-6 text-base"
              >
                <FileSpreadsheet className="mr-2 h-5 w-5" />
                Upload Quote
              </Button>
            )}
            {hasAnyRole(['Site Supervisor', 'Super Admin']) && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/fund-requests/create')} 
                  data-testid="quick-action-fund-request"
                  className="border-2 border-[#10b981] text-[#10b981] hover:bg-green-50 shadow-md hover:shadow-lg transition-all duration-200 px-6 py-6 text-base"
                >
                  <Banknote className="mr-2 h-5 w-5" />
                  New Fund Request
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/material-requests/create')} 
                  data-testid="quick-action-material-request"
                  className="border-2 border-[#1B273D] text-[#1B273D] hover:bg-gray-50 shadow-md hover:shadow-lg transition-all duration-200 px-6 py-6 text-base"
                >
                  <Truck className="mr-2 h-5 w-5" />
                  New Material Request
                </Button>
              </>
            )}
            <Button 
              variant="outline" 
              onClick={() => navigate('/quotes')} 
              data-testid="quick-action-view-quotes"
              className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg transition-all duration-200 px-6 py-6 text-base"
            >
              View All Quotes
            </Button>
          </div>
        </Card>
      </div>
    </Sidebar>
  );
}
